import cookie from "react-cookies";
import SparkMD5 from "spark-md5";

import { FileProps } from "models/Base";
import { axios } from "services/middleware";

interface ChunkedUploaderProps {
  url: string;
  urlCallback: string;
  file: FileProps;
  chunkSize?: number;
  onProgress: (progress: number) => void;
}

class ChunkedUploader {
  private readonly url: string;
  private readonly urlCallback: string;
  private readonly file: FileProps;
  private readonly chunkSize: number;
  private readonly onProgress: (progress: number) => void;
  private md5: string;
  public previewUrl: string;
  private readonly csrfToken: string;
  private paused: boolean;
  private currentChunk: number;
  private uploadId: string | undefined;
  private readonly maxRetries: number;

  constructor({ url, file, onProgress, urlCallback }: ChunkedUploaderProps) {
    this.url = url;
    this.file = file;
    this.chunkSize = 1024 * 1024; // Default chunk size of 1MB
    this.onProgress = onProgress;
    this.urlCallback = urlCallback;
    this.csrfToken = cookie.load("csrftoken");
    this.md5 = "";
    this.previewUrl = "";
    this.paused = false;
    this.currentChunk = 0;
    this.uploadId = undefined;
    this.maxRetries = 3; // Maximum number of retries for a failed chunk
  }

  // Method to pause the upload
  public pause() {
    this.paused = true;
  }

  // Method to resume the upload
  public resume() {
    if (this.paused) {
      this.paused = false;
      this.upload();
    }
  }

  // Method to calculate the MD5 hash of the file
  private async calculateMd5(): Promise<string> {
    return new Promise((resolve, reject) => {
      const spark = new SparkMD5.ArrayBuffer();
      const chunks = Math.ceil(this.file.size / this.chunkSize);
      let currentChunk = 0;

      const readNextChunk = () => {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          if (e.target && e.target.result instanceof ArrayBuffer) {
            spark.append(e.target.result); // Append chunk to MD5 hash
            currentChunk++;
            if (currentChunk < chunks) {
              readNextChunk(); // Process the next chunk
            } else {
              resolve(spark.end()); // Resolve the promise with the MD5 hash
            }
          }
        };
        reader.onerror = (e) => {
          // Create a new Error object with a descriptive message
          reject(new Error(`File reading error: ${e.target?.error?.message ?? "Unknown error"}`));
        };

        const start = currentChunk * this.chunkSize;
        const end = Math.min(start + this.chunkSize, this.file.size);
        reader.readAsArrayBuffer(this.file.slice(start, end));
      };

      readNextChunk();
    });
  }

  // Method to read a specific chunk of the file
  private readFileChunk(start: number, end: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const chunk = this.file.slice(start, end);

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error("FileReader result is not an ArrayBuffer"));
        }
      };
      reader.onerror = (e) => {
        // Create a new Error object with a descriptive message
        reject(new Error(`File reading error: ${e.target?.error?.message ?? "Unknown error"}`));
      };

      reader.readAsArrayBuffer(chunk);
    });
  }

  // Method to upload a specific chunk with retry logic
  private async uploadChunkWithRetry(
    arrayBuffer: ArrayBuffer,
    start: number,
    end: number,
    totalSize: number,
    uploadId?: string,
    customMimeType?: string,
    retries: number = this.maxRetries
  ): Promise<any> {
    let attempt = 0;
    while (attempt < retries) {
      try {
        return await this.uploadChunk(arrayBuffer, start, end, totalSize, uploadId, customMimeType);
      } catch (error) {
        attempt++;
        if (attempt === retries) {
          throw error;
        }
        // Implement a delay between retries (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  // Method to upload a chunk of the file to the server
  private async uploadChunk(
    arrayBuffer: ArrayBuffer,
    start: number,
    end: number,
    totalSize: number,
    uploadId?: string,
    customMimeType?: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append("csrfmiddlewaretoken", this.csrfToken);
    // Specify the custom MIME type, or use the file's original MIME type if not provided
    const mimeType = customMimeType ?? this.file.type;

    // Convert ArrayBuffer to Blob and append to formData
    const blob = new Blob([arrayBuffer], { type: mimeType });
    formData.append("attachment", blob, this.file.name); // Add file chunk to the form data
    if (uploadId) {
      formData.append("upload_id", uploadId); // Add upload ID if it exists
    }
    return axios.post(this.url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Content-Range": `bytes ${start}-${end - 1}/${totalSize}`
      },
      withCredentials: true
    });
  }

  // Method to finalize the upload by sending the MD5 hash and other data
  private async finalizeUpload(uploadId: string): Promise<any> {
    this.md5 = await this.calculateMd5(); // Calculate MD5 hash of the file
    const formData = new FormData();
    formData.append("csrfmiddlewaretoken", this.csrfToken);
    formData.append("upload_id", uploadId);
    formData.append("md5", this.md5);
    if (this.file?.categories) {
      formData.append("category", this.file?.categories);
    }
    if (this.file?.visible_to) {
      this.file?.visible_to.forEach((item) => {
        formData.append("visible_to", item);
      });
    }
    return await axios.post(this.urlCallback, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      withCredentials: true
    });
  }

  // Method to persist the upload state in localStorage
  private saveUploadState() {
    const state = {
      currentChunk: this.currentChunk,
      uploadId: this.uploadId,
      fileName: this.file.name,
      fileSize: this.file.size
    };
    localStorage.setItem("chunkedUploadState", JSON.stringify(state));
  }

  // Method to restore the upload state from localStorage
  private restoreUploadState(): boolean {
    const savedState = localStorage.getItem("chunkedUploadState");

    if (savedState) {
      const state = JSON.parse(savedState);
      if (state.fileName === this.file.name && state.fileSize === this.file.size) {
        this.currentChunk = state.currentChunk;
        this.uploadId = state.uploadId;
        return true;
      }
    }
    return false;
  }

  // Method to start the upload process
  public async upload(): Promise<void> {
    const totalSize = this.file.size;
    const totalChunks = Math.ceil(totalSize / this.chunkSize);

    // Try to restore the previous upload state
    if (!this.restoreUploadState()) {
      this.currentChunk = 0;
      this.uploadId = undefined;
    }

    while (this.currentChunk < totalChunks && !this.paused) {
      const start = this.currentChunk * this.chunkSize;
      const end = Math.min(start + this.chunkSize, totalSize);

      const chunk = await this.readFileChunk(start, end);
      const response = await this.uploadChunkWithRetry(chunk, start, end, totalSize, this.uploadId);
      this.uploadId = response.data.upload_id;

      this.currentChunk++;
      this.saveUploadState(); // Save state after each chunk

      const progress = (this.currentChunk / totalChunks) * 100;
      this.onProgress(progress);

      if (this.currentChunk === totalChunks && this.uploadId) {
        const res = await this.finalizeUpload(this.uploadId);
        this.previewUrl = res?.data?.preview_url;
        localStorage.removeItem("chunkedUploadState"); // Clear saved state after completion
      }
    }
  }
}

export default ChunkedUploader;
